import { useEffect, useState } from "react";
import { Check, Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DayField } from "@/components/DayField";
import { OneTimeForm } from "@/components/OneTimeForm";
import { DailyEntryForm } from "@/components/DailyEntryForm";
import { SuggestInput } from "@/components/SuggestInput";
import { api, apiError, fmtPct } from "@/lib/api";
import { toastWithUndo } from "@/lib/undo";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
  title: "Use case name",
  code: "Use case code",
  plant: "Unit",
  status: "Status",
  identified_on: "Project identification date",
  start_on: "Start date",
  target_completion_on: "Expected date of completion",
  phase: "Sub-stage",
  project_type: "Project type",
  provider_type: "Solution provider type",
  vendor: "Solution provider",
  focus_area: "Application area",
  technology_area: "Technology area",
  dept_shop: "Department or shop",
  owner_name: "Project Owner",
  approval_route: "Nature of investment",
  project_cost_lakh: "Cost in ₹ Lakh",
  est_value_cr: "Expected benefit in ₹ Crore",
  actual_benefit_cr: "Benefit vetted",
  finance_vetted_on: "Date of financial vetting",
  scope: "What it does",
  remarks: "Where it stands today",
  hold_reason: "Hold or drop reason",
  hold_drop_on: "Hold or drop date",
  discontinued_reason: "Why discontinued",
  discontinued_on: "Discontinued on",
  share_ministry: "Shared with Ministry",
  share_corporate: "Shared with Corporate Office",
  last_updated_by: "Last updated by",
  last_updated_on: "Last updated on",
  entered_by: "Entered by",
  entered_on: "Entered on",
  approved_by: "Approved by",
  approved_on: "Approved on",
};

const STAMPS = ["last_updated_by", "last_updated_on", "entered_by", "entered_on",
                "approved_by", "approved_on"];

const EMPTY = {
  title: "",
  code: "",
  plant: "",
  status: "",
  term: "",
  identified_on: "",
  start_on: "",
  target_completion_on: "",
  phase: "",
  project_type: "",
  provider_type: "",
  vendor: "",
  focus_area: "",
  technology_area: "",
  dept_shop: "",
  owner_name: "",
  approval_route: "",
  project_cost_lakh: "",
  est_value_cr: "",
  actual_benefit_cr: "",
  finance_vetted_on: "",
  scope: "",
  remarks: "",
  hold_reason: "",
  hold_drop_on: "",
  discontinued_reason: "",
  discontinued_on: "",
  share_ministry: true,
  share_corporate: true,
};

const DISCONTINUED = "Completed & Discontinued";
const WITH_SUB_STAGE = ["Identified", "Ongoing"];
// Pravartanam target-value work is delivered by the partner, so the provider is filled in for it.
const TV_PROVIDER = "McKinsey & Co.";
const TV_TYPE = "Pravartanam-TV";
// The eleven steps, and the control each entry sits on — the checklist and the ring both read this.
const STEP_TITLES = [
  [1, "Unit", "pf-step-unit"],
  [2, "Name of the use case", "pf-step-name"],
  [3, "Project type", "pf-step-type"],
  [4, "Who is building it", "pf-step-provider"],
  [5, "Where it stands", "pf-step-status"],
  [6, "Dates", "pf-step-dates"],
  [7, "What it is about", "pf-step-areas"],
  [8, "Investment and Returns", "pf-step-money"],
  [9, "Milestone dates", "pf-step-milestones"],
  [10, "In your own words", "pf-step-words"],
  [11, "What may be shared", "pf-step-sharing"],
];
// Controls that are not part of the walk down the form, but the Fix button may still be sent to.
const ASIDE_TEST_IDS = {
  hold_reason: "pf-hold-reason",
  hold_drop_on: "pf-hold-date",
  discontinued_reason: "pf-discontinued-reason",
  discontinued_on: "pf-discontinued-on",
  code: "pf-code",
  term: "pf-term",
};

const COMPLETION_STAGE = {
  "Completed & Running": "Completed and Running",
  "Completed & Discontinued": "Completed and Discontinued",
};
// What is still asked of a completed use case outside Pravartanam-TV.
const LIGHT_NEEDS = new Set(["plant", "title", "project_type", "provider_type", "status",
  "completed_on"]);
const PREFILLED_HINT =
  "Prefilled by the portal — one year before the completion date. Overwrite it if you know the real date.";
const SMARTFIX_HINT =
  "SmartFixed by the portal from the dates on record. Overwrite it if you know the real date.";

const dayOf = (value) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(value || "").trim());
  return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
};

/** Which of the three roads this use case travels — exactly as the portal decides it. */
const roadKeyOf = (form) => {
  if ((form.project_type || "").startsWith("Pravartanam-TV")) return "delivery";
  const provider =
    form.provider_type || ((form.vendor || "").trim() ? "Non-Startup" : "In-house");
  return provider === "In-house" ? "self" : "procurement";
};

const Field = ({ label, hint, children, wide, gap, confirmed, onConfirmGap, focus }) => (
  <div
    className={`${wide ? "sm:col-span-2" : ""}${gap ? " border-l-2 pl-3" : ""}${
      focus ? " next-fill" : ""
    }`}
    style={gap ? { borderLeftColor: confirmed ? "hsl(var(--healthy))" : "hsl(var(--watch))" } : undefined}
    data-testid={gap ? `pf-gap-${gap.field}` : undefined}
    data-next={focus ? "yes" : undefined}
  >
    <label className="label-caps">{label}</label>
    {children}
    {gap && (
      <p className="mt-1 text-[10px]" style={{ color: confirmed ? "hsl(var(--healthy))" : "hsl(var(--watch))" }}>
        {confirmed ? "Marked as right as it stands" : `Still to be filled — ${gap.note}`}
        {!confirmed && onConfirmGap && (
          <button
            type="button"
            data-testid={`pf-confirm-gap-${gap.field}`}
            onClick={() => onConfirmGap(gap.field)}
            className="ml-2 underline decoration-dotted transition-opacity duration-200 hover:opacity-70"
          >
            It is right as it stands
          </button>
        )}
      </p>
    )}
    {hint && <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

/** One step of the form. It stays greyed and out of reach until the step above it is answered. */
const Step = ({ n, title, note, open, lock, children, testId, single }) => (
  <section
    data-testid={testId}
    data-open={open ? "yes" : "no"}
    className="border-t border-border pt-4"
  >
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className={`font-data text-[11px] ${open ? "text-primary" : "text-muted-foreground"}`}>
        {String(n).padStart(2, "0")}
      </span>
      <h3
        className={`font-display text-sm font-bold tracking-tight ${
          open ? "" : "text-muted-foreground"
        }`}
      >
        {title}
      </h3>
      {!open && lock && (
        <span className="text-[10px] text-muted-foreground" data-testid={`${testId}-lock`}>
          {lock}
        </span>
      )}
      {open && note && <span className="text-[10px] text-muted-foreground">{note}</span>}
    </div>
    <div
      className={`mt-3 grid gap-4 ${single ? "" : "sm:grid-cols-2"} ${
        open ? "" : "pointer-events-none select-none opacity-40"
      }`}
    >
      {children}
    </div>
  </section>
);

/** The one form for a use case — adding it, and every later change, top to bottom. */
export const ProjectFormDialog = ({ open, onOpenChange, project, meta, onSaved,
                                    focusField = "" }) => {
  const { user, scopedPlant, superAdmin, isPartner } = useAuth();
  // A SPOC or designated member holding several units adds a use case to any one of them.
  const ownUnits = meta?.edit_plants || [];
  const mode = project ? "edit" : "add";
  const isSystemAdmin = user?.role === "system_admin";
  const [form, setForm] = useState(EMPTY);
  const [phaseReason, setPhaseReason] = useState("");
  const [phaseExpected, setPhaseExpected] = useState("");
  const [phasePrevDone, setPhasePrevDone] = useState("");
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  // Compliance belongs on this form too, but only when the use case is actually sending readings.
  // A hole found on Data Inconsistency is then filled here instead of on another screen.
  const [watch, setWatch] = useState(null);
  const [watchBeat, setWatchBeat] = useState(0);
  useEffect(() => {
    if (!open || mode !== "edit" || !project?.id) {
      setWatch(null);
      return;
    }
    api
      .get(`/compliance/trend/${project.id}`)
      .then((r) => setWatch(r.data))
      .catch(() => setWatch(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, project?.id, watchBeat]);

  // What a workbook could not state properly — highlighted here so it is filled and cleared.
  const gapOf = (field) => (project?.data_gaps || []).find((g) => g.field === field);
  const [okGaps, setOkGaps] = useState([]);
  const confirmGap = (field) =>
    setOkGaps((prev) => (prev.includes(field) ? prev : [...prev, field]));
  const gapProps = (field) => {
    const gap = gapOf(field);
    if (!gap) return {};
    return { gap, confirmed: okGaps.includes(field), onConfirmGap: confirmGap };
  };
  // A Safety usecase is certified by the Unit Safety Head instead of being vetted by Finance.
  const isSafety = form.focus_area === "Safety & Surveillance";
  // A mine group is worked as a set of individual mines, and those mines are its departments. So
  // on a Mines use case the Dept / Shop entry is a list of that group's own mines, and it is
  // compulsory — a plant's department stays free text.
  const mines = (meta?.sub_divisions || {})[form.plant] || [];

  const roadKey = roadKeyOf(form);
  const road = (meta?.roads || []).find((r) => r.key === roadKey);
  const roadStages = road?.stages || [];
  const roadLabel = road?.label || "";
  const withSubStage = (meta?.statuses_with_sub_stage || WITH_SUB_STAGE).includes(form.status);
  const subStages =
    form.status === "Identified" ? meta?.identified_sub_stages || [] :
    form.status === "Ongoing" ? roadStages : [];
  const milestones = roadStages;
  const held = project?.stage_dates || {};
  // A finished use case is dated on its completion sub-stage. Outside Pravartanam-TV that date is
  // the only one asked for — the start and identification dates are then prefilled by the portal.
  const completionStage = COMPLETION_STAGE[form.status] || "";
  const light =
    !!completionStage && !String(form.project_type || "").startsWith("Pravartanam-TV");
  const completedOn = (dates[completionStage] || {}).completed_on ?? "";
  const heldCompletedOn = (held[completionStage] || {}).completed_on || "";
  const prefilled = (field) =>
    (project?.prefilled_fields || []).includes(field) && form[field] === project?.[field];
  const smartfixed = (field) =>
    (project?.smartfixed_fields || []).includes(field) && form[field] === project?.[field];

  // Top to bottom: a step opens only once the one above it has been answered.
  const can = {
    unit: true,
    name: !!form.plant,
    type: !!form.plant && !!form.title.trim(),
    provider: !!form.plant && !!form.title.trim() && !!form.project_type,
  };
  can.status = can.provider && !!form.provider_type;
  can.rest = can.status && !!form.status;

  // ── the next control to be filled ──────────────────────────────────────────────────────────
  // The form is filled in one pass, so it says where it is waiting: one control carries the ring,
  // the checklist down the side ticks off each step, and the ring moves on as entries are
  // answered. The step a control sits in must already be open, or it is skipped for now.
  const stepOpen = {
    1: can.unit, 2: can.name, 3: can.type, 4: can.provider, 5: can.status, 6: can.rest,
    7: can.rest, 8: can.rest, 9: can.rest && !!milestones.length, 10: can.rest, 11: can.rest,
    12: !!watch?.card,
  };
  const steps = watch?.card
    ? [...STEP_TITLES, [12, "Compliance data", "pf-step-compliance"]]
    : STEP_TITLES;
  const FLOW = [
    { field: "plant", step: 1, testId: "pf-plant", need: true },
    { field: "title", step: 2, testId: "pf-title", need: true },
    { field: "project_type", step: 3, testId: "pf-type", need: true },
    { field: "provider_type", step: 4, testId: "pf-provider-type", need: true },
    { field: "vendor", step: 4, testId: "pf-vendor", need: false },
    { field: "status", step: 5, testId: "pf-status", need: true },
    { field: "phase", step: 5, testId: "pf-phase", need: withSubStage },
    { field: "phase_expected", step: 5, testId: "pf-phase-expected", need: withSubStage,
      label: "Date this sub-stage is expected to close" },
    { field: "completed_on", step: 6, testId: "pf-completed-on", need: !!completionStage,
      label: "Date of completion", value: completedOn || project?.completed_on || "" },
    { field: "identified_on", step: 6, testId: "pf-identified-date", need: true },
    { field: "start_on", step: 6, testId: "pf-start-date", need: true },
    { field: "target_completion_on", step: 6, testId: "pf-target-date", need: true },
    { field: "focus_area", step: 7, testId: "pf-focus", need: true },
    { field: "technology_area", step: 7, testId: "pf-technology", need: false },
    { field: "dept_shop", step: 7, testId: "pf-dept-shop", need: !!mines.length,
      label: mines.length ? "Mine" : "Department or shop" },
    { field: "owner_name", step: 7, testId: "pf-owner-name", need: true },
    { field: "approval_route", step: 8, testId: "pf-approval-route", need: false },
    { field: "project_cost_lakh", step: 8, testId: "pf-cost", need: false },
    { field: "est_value_cr", step: 8, testId: "pf-benefit", need: !isSafety },
    { field: "actual_benefit_cr", step: 8, testId: "pf-actual-benefit", need: false },
    { field: "finance_vetted_on", step: 8, testId: "pf-finance-vetted-on",
      need: !!String(form.actual_benefit_cr ?? "").trim() },
    { field: "scope", step: 10, testId: "pf-scope", need: false },
    { field: "remarks", step: 10, testId: "pf-remarks", need: true },
    // On compliance tracking, the scale it is read against and at least one day belong here too.
    ...(watch?.card
      ? [
          { field: "compliance_target_kpi", step: 12, label: "Target KPI", need: true,
            testId: `pf-one-time-${watch.card.code}-target_kpi`,
            value: watch.card.kpi.target },
          { field: "compliance_baseline_kpi", step: 12, label: "Baseline KPI", need: true,
            testId: `pf-one-time-${watch.card.code}-baseline_kpi`,
            value: watch.card.kpi.baseline },
          { field: "compliance_target_impact_lakh", step: 12, label: "Target Impact ₹ Lakh",
            need: true, testId: `pf-one-time-${watch.card.code}-target_impact_lakh`,
            value: watch.card.impact.target_lakh },
          { field: "compliance_day", step: 12, label: "A day's reading on record", need: true,
            testId: `pf-daily-${watch.card.code}-date`,
            value: watch.card.compliance.days || "" },
        ]
      : []),
  ];
  const valueOf = (field) =>
    String((field === "phase_expected" ? phaseExpected : form[field]) ?? "").trim();
  const flow = FLOW.map((f) => ({
    ...f,
    need: f.need && (!light || LIGHT_NEEDS.has(f.field)),
    label: f.label || LABELS[f.field] || f.field,
    done: "value" in f ? !!String(f.value ?? "").trim() : !!valueOf(f.field),
    open: !!stepOpen[f.step],
  }));
  const nextField = (flow.find((f) => f.need && f.open && !f.done) || {}).field || "";
  // A Fix button sends the officer to one entry — it keeps the ring until it is answered.
  const highlight = focusField && !valueOf(focusField) ? focusField : nextField;
  const highlighted = flow.find((f) => f.field === highlight);
  const highlightLabel =
    highlighted?.label ||
    LABELS[highlight] ||
    (highlight?.startsWith("milestone:")
      ? `${highlight.slice("milestone:".length)} actual date`
      : highlight
        ? highlight.replace(/_/g, " ")
        : "");
  const testIdOf = (field) =>
    field?.startsWith("milestone:")
      ? `pf-milestone-actual-${field.slice("milestone:".length)}`
      : flow.find((f) => f.field === field)?.testId || ASIDE_TEST_IDS[field] || "";

  // The ring sits on the one entry the form is waiting on, and moves on as each is answered.
  const hl = (field) => ({ focus: highlight === field });

  const jumpTo = (testId) => {
    if (!testId) return;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function" && ["INPUT", "TEXTAREA"].includes(el.tagName)) {
        el.focus({ preventScroll: true });
      }
    }
  };

  // Opened from the Data Inconsistency list: go straight to the entry that is wanted.
  useEffect(() => {
    if (!open || !focusField) return undefined;
    const t = setTimeout(() => jumpTo(testIdOf(focusField)), 280);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusField]);

  // Short-Term or Long-Term is never typed. It follows the expected date of completion, judged
  // against the financial year it was first decided in — a new financial year does not move it.
  const termFyEnd = project?.term_fy_end || meta?.fy_end || "";
  const fyEnd = dayOf(termFyEnd);
  const computedTerm = (() => {
    const ends = dayOf(form.target_completion_on);
    if (!ends || !fyEnd) return "";
    return ends <= fyEnd ? "Short-Term" : "Long-Term";
  })();
  const shownTerm = computedTerm || project?.term || form.term;
  // Settled: on record and still what the date says. Otherwise the save writes the new one.
  const termLocked = !!project?.term_locked && (!computedTerm || computedTerm === project?.term);
  const termMoves = mode === "edit" && !!computedTerm && computedTerm !== (project?.term || "");

  useEffect(() => {
    if (!open) return;
    setPhaseReason("");
    setOkGaps([]);
    if (project) {
      // Every milestone starts from what is already on record, so nothing is lost by saving.
      setDates(
        Object.fromEntries(
          Object.entries(project.stage_dates || {}).map(([stage, rec]) => [
            stage,
            { expected_on: rec.expected_on || "", completed_on: rec.completed_on || "" },
          ]),
        ),
      );
      setForm({
        ...EMPTY,
        ...Object.fromEntries(STAMPS.map((k) => [k, project[k] ?? ""])),
        ...Object.fromEntries(
          Object.keys(EMPTY).map((k) => [
            k,
            k === "share_ministry" || k === "share_corporate"
              ? project[k] !== false
              : project[k] ?? "",
          ]),
        ),
        // The six words a reviewer reads, and the investment nature the register holds.
        status: project.status_view || "",
        phase: project.phase_view || "",
        approval_route: project.investment_nature || "",
      });
      setPhaseExpected((project.stage_dates?.[project.phase] || {}).expected_on || "");
      setPhasePrevDone("");
    } else {
      setDates({});
      setForm({
        ...EMPTY,
        plant: scopedPlant || meta?.my_plant || "",
        status: "",
        // A partner adds Pravartanam-TV work only — McKinsey's, prefilled and still editable.
        ...(isPartner ? { project_type: TV_TYPE, vendor: TV_PROVIDER, provider_type: "Non-Startup" } : {}),
      });
      setPhaseExpected("");
      setPhasePrevDone("");
    }
  }, [open, project, scopedPlant, meta, isPartner]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDate = (stage, key, value) =>
    setDates((d) => ({ ...d, [stage]: { ...(d[stage] || {}), [key]: value } }));

  // A sub-stage belongs to one road and one standing. Changing either lets the old pick go.
  const setStatus = (value) => {
    const offered =
      value === "Identified" ? meta?.identified_sub_stages || [] :
      value === "Ongoing" ? roadStages : [];
    setForm((f) => ({ ...f, status: value, phase: offered.includes(f.phase) ? f.phase : "" }));
  };
  const setRoadField = (key, value) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Built in-house means no outside company — a name kept from a vendor would mislead.
      if (key === "provider_type" && value === "In-house") next.vendor = "";
      // Pravartanam target-value work is McKinsey's — prefilled, and still editable.
      if (key === "project_type" && String(value || "").startsWith("Pravartanam-TV")) {
        if (!(next.vendor || "").trim()) next.vendor = TV_PROVIDER;
        if (!next.provider_type) next.provider_type = "Non-Startup";
      }
      const offered =
        next.status === "Identified" ? meta?.identified_sub_stages || [] :
        next.status === "Ongoing" ? (meta?.roads || []).find((r) => r.key === roadKeyOf(next))?.stages || [] : [];
      return { ...next, phase: offered.includes(next.phase) ? next.phase : "" };
    });

  const phaseToSend = withSubStage ? form.phase || null : null;
  const phaseMoved = mode === "edit" && !!phaseToSend && phaseToSend !== project?.phase;
  const heldPhaseExpected = (held[form.phase] || {}).expected_on || "";
  const phaseDateMoves =
    !!phaseExpected.trim() && !!heldPhaseExpected && phaseExpected.trim() !== heldPhaseExpected;
  const goingDiscontinued =
    form.status === DISCONTINUED && project?.status_view !== DISCONTINUED;
  const onHold = form.status === "Put on Hold" || form.status === "Dropped";

  const milestoneBody = () =>
    [...milestones, ...(completionStage && !milestones.includes(completionStage) ? [completionStage] : [])]
      .map((stage) => {
        const row = dates[stage] || {};
        const rec = held[stage] || {};
        const expected = (row.expected_on ?? "").trim();
        const completed = (row.completed_on ?? "").trim();
        if (!expected && !completed) return null;
        if (expected === (rec.expected_on || "") && completed === (rec.completed_on || "")) {
          return null;
        }
        const entry = { stage, reason: (row.reason || "").trim() || null };
        // Only what the officer actually touched travels, so nothing on record is wiped.
        if (expected && expected !== (rec.expected_on || "")) entry.expected_on = expected;
        if (completed && completed !== (rec.completed_on || "")) entry.completed_on = completed;
        return entry.expected_on || entry.completed_on ? entry : null;
      })
      .filter(Boolean);

  const changeList = () => {
    const out = [];
    Object.keys(LABELS).forEach((k) => {
      if (!(k in form)) return;
      if (STAMPS.includes(k) && !superAdmin) return;
      const was =
        k === "status" ? project?.status_view :
        k === "phase" ? project?.phase_view :
        k === "approval_route" ? project?.investment_nature : project?.[k];
      const now = form[k];
      const same =
        typeof was === "boolean" || typeof now === "boolean"
          ? !!was === !!now
          : String(was ?? "") === String(now ?? "");
      if (!same) out.push({ field: k, label: LABELS[k], from: was, to: now });
    });
    if (phaseDateMoves) {
      out.push({
        field: "phase_expected_on",
        label: `${form.phase} expected to close on`,
        from: heldPhaseExpected,
        to: phaseExpected.trim(),
      });
    }
    milestoneBody().forEach((m) =>
      out.push({
        field: `date::${m.stage}`,
        label: `${m.stage} date`,
        from: (held[m.stage] || {}).expected_on || (held[m.stage] || {}).completed_on || null,
        to: m.expected_on || m.completed_on,
      }),
    );
    if (termMoves && !out.some((c) => c.field === "term")) {
      out.push({ field: "term", label: "Term", from: project?.term || null, to: computedTerm });
    }
    return out;
  };

  const askSave = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.plant || !form.status) {
      toast.error("The unit, the use-case name and the status are needed.");
      return;
    }
    if (withSubStage && !form.phase) {
      toast.error(
        form.status === "Identified"
          ? "Please say where this identified use case stands."
          : `Please pick the sub-stage it stands at on the ${roadLabel} road.`,
      );
      return;
    }
    if (goingDiscontinued && !form.discontinued_reason.trim()) {
      toast.error("Please say why this use case has been discontinued.");
      return;
    }
    if (onHold && !form.hold_reason.trim()) {
      toast.error(`Please say why it is ${form.status.toLowerCase()}, and from when.`);
      return;
    }
    if (phaseDateMoves && !phaseReason.trim()) {
      toast.error(
        `${form.phase} is on record for ${heldPhaseExpected}. Please say why the date is moving.`,
      );
      return;
    }
    const missing = milestoneBody().find((m) => {
      const rec = held[m.stage] || {};
      const moving =
        (m.expected_on && rec.expected_on && m.expected_on !== rec.expected_on) ||
        (m.completed_on && rec.completed_on && m.completed_on !== rec.completed_on);
      return moving && !m.reason;
    });
    if (missing) {
      toast.error(`${missing.stage} is already on record. Please say why its date is moving.`);
      return;
    }
    const list = changeList();
    if (mode === "edit" && !list.length && !(project?.data_gaps || []).length) {
      toast.info("Nothing has been changed yet.");
      return;
    }
    setAsk(list);
  };

  const numeric = (v) => (v === "" || v === null ? null : Number(v));

  const body = () => {
    const out = { ...form };
    STAMPS.forEach((k) => delete out[k]);
    // The term belongs to the portal, worked out from the dates — it never travels from a form.
    delete out.term;
    if (okGaps.length) out.confirm_gaps = okGaps;
    if (mode === "edit" && superAdmin) {
      // The portal's own stamps travel only when a Super-Admin has changed one of them.
      STAMPS.filter((k) => (form[k] || "") !== (project?.[k] || "")).forEach((k) => {
        out[k] = form[k] || null;
      });
    }
    delete out.code;
    if (mode === "edit" && isSystemAdmin) out.code = form.code;
    ["project_cost_lakh", "est_value_cr", "actual_benefit_cr"].forEach((k) => {
      out[k] = numeric(out[k]);
    });
    Object.keys(out).forEach((k) => {
      if (out[k] === "") out[k] = null;
    });
    out.share_ministry = !!form.share_ministry;
    out.share_corporate = !!form.share_corporate;
    // A held, dropped or finished use case stands on no sub-stage; the two completion words carry
    // their own, which the portal sets from the status itself.
    out.phase = phaseToSend;
    out.phase_expected_on = phaseExpected.trim() || null;
    out.phase_prev_completed_on = phasePrevDone.trim() || null;
    out.phase_reason = phaseReason.trim() || null;
    const ms = milestoneBody();
    if (ms.length) out.milestones = ms;
    if (mode === "edit" && !phaseMoved) {
      // The sub-stage has not moved, so only its date travels with the save.
      delete out.phase;
      delete out.phase_reason;
      delete out.phase_prev_completed_on;
      if (!phaseDateMoves && !phaseExpected.trim()) delete out.phase_expected_on;
      else if (form.phase) {
        out.milestones = [
          ...(out.milestones || []),
          {
            stage: form.phase,
            expected_on: phaseExpected.trim() || null,
            reason: phaseReason.trim() || null,
          },
        ];
        delete out.phase_expected_on;
      }
    }
    if (mode === "edit" && !isSystemAdmin) {
      delete out.title;
      delete out.code;
      delete out.plant;
    }
    return out;
  };

  const save = async (list) => {
    setBusy(true);
    setAsk(null);
    try {
      if (mode === "add") {
        const { data } = await api.post("/projects", body());
        if (data.awaiting_verification) {
          toastWithUndo(
            data.pending?.queue === "partner" || isPartner
              ? "Recorded and sent to the Pravartanam Partner Manager for approval — it joins the register once approved."
              : `Sent to the ${form.plant} SPOC for approval — it joins the register once approved.`,
            async () => {
              await api.post(`/pending/${data.pending.id}/withdraw`);
              onSaved?.(null);
            },
            { done: "Taken back." },
          );
        } else {
          toast.success(`${data.project.code} added to the ${data.project.plant} register.`);
        }
        onSaved?.(data.project);
      } else {
        const { data } = await api.patch(`/projects/${project.id}`, body());
        const waiting = data.awaiting_verification;
        const undo = async () => {
          if (waiting && data.pending?.id) {
            await api.post(`/pending/${data.pending.id}/withdraw`);
          } else {
            const back = {};
            list
              .filter((c) => c.field in LABELS && !c.field.startsWith("date::"))
              .forEach((c) => {
                back[c.field] = c.from === undefined ? null : c.from;
              });
            if (Object.keys(back).length) await api.patch(`/projects/${project.id}`, back);
          }
          onSaved?.();
        };
        toastWithUndo(
          waiting
            ? isPartner
              ? "Recorded and sent to the Pravartanam Partner Manager for approval — it stays out of the register and the packs until then."
              : "Recorded and sent to your SPOC for approval — it stays out of the register and the packs until then."
            : superAdmin
              ? "Saved. Nothing was recorded — Super-Admin mode is on."
              : list.length
                ? `Saved. ${list.length} change(s) recorded.`
                : "Saved. Marked as right as it stands.",
          undo,
          { done: "Put back the way it was." },
        );
        onSaved?.();
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto rounded-none bg-card sm:max-w-3xl lg:max-w-5xl"
        data-testid="project-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">
            {mode === "add" ? "Add a use case" : "Update this use case"}
          </DialogTitle>
          <DialogDescription>
            {mode === "add"
              ? "Fill it from the top down — each step opens the next."
              : `${project?.code} · ${project?.title}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_210px]">
        <form onSubmit={askSave} className="space-y-1">
          <Step n={1} title="Unit" open={can.unit} testId="pf-step-unit">
            <Field label="Unit *" {...hl("plant")}>
              <Select
                value={form.plant}
                onValueChange={(v) => set("plant", v)}
                disabled={mode === "edit" ? !isSystemAdmin : !!scopedPlant && ownUnits.length < 2}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-plant">
                  <SelectValue placeholder="Choose the unit" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  {meta?.plants
                    ?.filter((p) => mode === "edit" || !scopedPlant || ownUnits.includes(p.code))
                    .map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.code} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {mode === "edit" && (
              <Field label="Use case code">
                <Input
                  className="font-data mt-1 h-11 rounded-none"
                  data-testid="pf-code"
                  value={form.code}
                  disabled={!isSystemAdmin}
                  onChange={(e) => set("code", e.target.value)}
                />
              </Field>
            )}
          </Step>

          <Step
            n={2}
            title="Name of the use case"
            open={can.name}
            lock="Choose the unit first"
            testId="pf-step-name"
            single
          >
            <Field label="Name *" {...gapProps("title")} {...hl("title")}>
              <Input
                className="mt-1 h-11 rounded-none"
                data-testid="pf-title"
                placeholder="e.g. Ferro alloy addition optimisation at SMS-II"
                value={form.title}
                disabled={!can.name || (mode === "edit" && !isSystemAdmin)}
                onChange={(e) => set("title", e.target.value)}
              />
            </Field>
          </Step>

          <Step
            n={3}
            title="Project type"
            open={can.type}
            lock="Name the use case first"
            testId="pf-step-type"
          >
            <Field
              label="Project type *"
              hint={isPartner ? "Partner access adds Pravartanam-TV use cases only" : undefined}
              {...gapProps("project_type")}
              {...hl("project_type")}
            >
              <Select
                value={form.project_type}
                onValueChange={(v) => setRoadField("project_type", v)}
                disabled={!can.type}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-type">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  {meta?.project_types
                    ?.filter((t) => !isPartner || t.startsWith(TV_TYPE))
                    .map((t) => (
                    <SelectItem key={t} value={t} data-testid={`pf-type-option-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Step>

          <Step
            n={4}
            title="Who is building it"
            note={roadLabel ? `It travels the ${roadLabel} path` : ""}
            open={can.provider}
            lock="Choose the project type first"
            testId="pf-step-provider"
          >
            <Field
              label="Solution provider type *"
              {...gapProps("provider_type")}
              {...hl("provider_type")}
            >
              <Select
                value={form.provider_type}
                onValueChange={(v) => setRoadField("provider_type", v)}
                disabled={!can.provider}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-provider-type">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  {meta?.provider_types?.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                      {meta.provider_type_help?.[t] ? ` — ${meta.provider_type_help[t]}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Solution provider" {...hl("vendor")}>
              <SuggestInput
                field="vendor"
                testId="pf-vendor"
                placeholder="e.g. Tata Consultancy Services"
                value={form.vendor ?? ""}
                disabled={!can.provider}
                onChange={(v) => setRoadField("vendor", v)}
              />
            </Field>
          </Step>

          <Step
            n={5}
            title="Where it stands"
            open={can.status}
            lock="Say who is building it first"
            testId="pf-step-status"
          >
            <Field label="Status *" {...gapProps("status")} {...hl("status")}>
              <Select value={form.status} onValueChange={setStatus} disabled={!can.status}>
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-status">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  {meta?.status_views?.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.key} — {s.help}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label={
                withSubStage
                  ? `Sub-stage * · ${form.status === "Identified" ? "identified" : roadLabel}`
                  : "Sub-stage"
              }
              hint={
                withSubStage
                  ? undefined
                  : "A sub-stage is only asked for while a use case is Identified or Ongoing."
              }
              {...gapProps("phase")}
              {...hl("phase")}
            >
              <Select
                value={form.phase}
                onValueChange={(v) => set("phase", v)}
                disabled={!can.status || !withSubStage}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-phase">
                  <SelectValue
                    placeholder={withSubStage ? "Choose" : "Not asked for at this status"}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  {subStages.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {withSubStage && (
              <Field
                label="This sub-stage is expected to close on"
                hint={heldPhaseExpected ? `On record: ${heldPhaseExpected}` : "dd.mm.yyyy"}
                {...hl("phase_expected")}
              >
                <DayField
                  className="mt-1"
                  tall
                  testId="pf-phase-expected"
                  value={phaseExpected}
                  disabled={!can.status}
                  onChange={setPhaseExpected}
                />
              </Field>
            )}
            {phaseMoved && (
              <Field
                label={`Date ${project?.phase} was completed`}
                hint={
                  String(form.project_type || "").startsWith("Pravartanam-TV")
                    ? "Needed for a Pravartanam target-value use case when the sub-stage moves on."
                    : "The day the sub-stage it is leaving was finished — optional off Pravartanam-TV."
                }
              >
                <DayField
                  className="mt-1"
                  tall
                  testId="pf-phase-prev-completed"
                  value={phasePrevDone}
                  onChange={setPhasePrevDone}
                />
              </Field>
            )}
            {(phaseDateMoves || phaseMoved || gapOf("revision_reason")) && (
              <Field
                label={
                  phaseDateMoves
                    ? `Why is the ${form.phase} date moving from ${heldPhaseExpected}? (compulsory)`
                    : gapOf("revision_reason")
                      ? "Why the expected date moved (came in from a workbook without it)"
                      : "A line for the trail (optional)"
                }
                {...gapProps("revision_reason")}
              >
                <Input
                  className="mt-1 h-11 rounded-none"
                  data-testid="pf-phase-reason"
                  placeholder="e.g. tender re-floated, vendor delivery slipped"
                  value={phaseReason}
                  onChange={(e) => setPhaseReason(e.target.value)}
                />
              </Field>
            )}
          </Step>

          {onHold && (
            <div className="border border-[hsl(var(--at-risk))] p-4" data-testid="pf-hold-block">
              <div className="text-xs font-semibold text-[hsl(var(--at-risk))]">
                {form.status === "Dropped" ? "Why is it being dropped?" : "Why is it on hold?"}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className="label-caps">Reason *</label>
                  <Textarea
                    className="mt-1 rounded-none"
                    rows={2}
                    data-testid="pf-hold-reason"
                    value={form.hold_reason}
                    onChange={(e) => set("hold_reason", e.target.value)}
                  />
                </div>
                <div>
                  <label className="label-caps">Date</label>
                  <DayField
                    className="mt-1"
                    tall
                    testId="pf-hold-date"
                    value={form.hold_drop_on}
                    onChange={(v) => set("hold_drop_on", v)}
                  />
                </div>
              </div>
            </div>
          )}

          {(form.status === DISCONTINUED || form.discontinued_reason) && (
            <div
              className="border border-[hsl(var(--at-risk))] p-4"
              data-testid="pf-discontinued-block"
            >
              <div className="text-xs font-semibold text-[hsl(var(--at-risk))]">
                Why has this use case been discontinued?
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className="label-caps">Reason *</label>
                  <Textarea
                    className="mt-1 rounded-none"
                    rows={2}
                    data-testid="pf-discontinued-reason"
                    placeholder="e.g. the shop was re-laid and the model no longer applies"
                    value={form.discontinued_reason}
                    onChange={(e) => set("discontinued_reason", e.target.value)}
                  />
                </div>
                <div>
                  <label className="label-caps">Taken out of use on</label>
                  <DayField
                    className="mt-1"
                    tall
                    testId="pf-discontinued-on"
                    value={form.discontinued_on}
                    onChange={(v) => set("discontinued_on", v)}
                  />
                </div>
              </div>
            </div>
          )}

          <Step
            n={6}
            title="Dates"
            open={can.rest}
            lock="Say where it stands first"
            testId="pf-step-dates"
          >
            {completionStage && (
              <Field
                label="Date of completion"
                hint={
                  light
                    ? "The only date asked of a completed use case outside Pravartanam-TV — the start and identification dates are then prefilled by the portal."
                    : "dd.mm.yyyy — the day it was completed."
                }
                {...hl("completed_on")}
              >
                <DayField
                  className="mt-1"
                  tall
                  testId="pf-completed-on"
                  placeholder={project?.completed_on ? `on record: ${project.completed_on}` : "dd.mm.yyyy"}
                  value={completedOn}
                  disabled={!can.rest}
                  onChange={(v) => setDate(completionStage, "completed_on", v)}
                />
                {!!heldCompletedOn && !!completedOn.trim() && completedOn.trim() !== heldCompletedOn && (
                  <Input
                    className="mt-2 h-10 rounded-none text-xs"
                    data-testid="pf-completed-on-reason"
                    placeholder={`why it moves from ${heldCompletedOn} (compulsory)`}
                    value={(dates[completionStage] || {}).reason ?? ""}
                    onChange={(e) => setDate(completionStage, "reason", e.target.value)}
                  />
                )}
              </Field>
            )}
            <Field
              label="Project identification date"
              hint={
                prefilled("identified_on")
                  ? PREFILLED_HINT
                  : smartfixed("identified_on")
                    ? SMARTFIX_HINT
                    : "dd.mm.yyyy — the day this usecase was identified and taken into the pipeline"
              }
              {...hl("identified_on")}
            >
              <DayField
                className="mt-1"
                tall
                testId="pf-identified-date"
                value={form.identified_on}
                disabled={!can.rest}
                onChange={(v) => set("identified_on", v)}
              />
            </Field>
            <Field
              label="Start date"
              hint={
                prefilled("start_on")
                  ? PREFILLED_HINT
                  : smartfixed("start_on")
                    ? SMARTFIX_HINT
                    : "dd.mm.yyyy — the day work begins. A date in the future is fine: a planned start."
              }
              {...hl("start_on")}
            >
              <DayField
                className="mt-1"
                tall
                testId="pf-start-date"
                value={form.start_on}
                disabled={!can.rest}
                onChange={(v) => set("start_on", v)}
              />
            </Field>
            <Field
              label="Expected date of completion"
              hint={
                smartfixed("target_completion_on")
                  ? SMARTFIX_HINT
                  : `On or before ${termFyEnd || "31.03.2027"} makes it Short-Term; after it, Long-Term.`
              }
              {...hl("target_completion_on")}
            >
              <DayField
                className="mt-1"
                tall
                testId="pf-target-date"
                value={form.target_completion_on}
                disabled={!can.rest}
                onChange={(v) => set("target_completion_on", v)}
              />
            </Field>
            <Field
              label="Term"
              hint={
                termMoves
                  ? `Was ${project?.term || "not settled"} — the expected date of completion now makes it ${computedTerm}. Saving settles it.`
                  : termLocked
                    ? `Settled as ${shownTerm} from the expected date of completion, against the year ending ${termFyEnd} — a new financial year does not move it.`
                    : shownTerm
                      ? `${shownTerm}, worked out from the expected date of completion: on or before ${termFyEnd} is Short-Term, after it Long-Term.`
                      : "Give the expected date of completion and the portal settles this itself."
              }
              {...gapProps("term")}
            >
              <div
                className="font-data mt-1 flex h-11 items-center justify-between border border-border bg-muted/40 px-3 text-sm"
                data-testid="pf-term"
              >
                <span className={shownTerm ? "" : "text-muted-foreground"}>
                  {shownTerm || "awaiting the dates"}
                </span>
                <span className="label-caps flex items-center gap-1">
                  {termLocked && <Lock className="h-3 w-3" />}
                  {termLocked ? "settled" : "worked out"}
                </span>
              </div>
            </Field>
          </Step>

          <Step
            n={7}
            title="What it is about"
            open={can.rest}
            lock="Say where it stands first"
            testId="pf-step-areas"
          >
            <Field label="Application area" {...hl("focus_area")}>
              <Select
                value={form.focus_area}
                onValueChange={(v) => set("focus_area", v)}
                disabled={!can.rest}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-focus">
                  <SelectValue placeholder="Where in the plant it applies" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  {meta?.focus_areas?.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Technology area" {...hl("technology_area")}>
              <Select
                value={form.technology_area}
                onValueChange={(v) => set("technology_area", v)}
                disabled={!can.rest}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-technology">
                  <SelectValue placeholder="What it is built with" />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-none bg-popover">
                  {meta?.technology_areas?.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label={mines.length ? "Mine *" : "Department or shop"}
              hint={
                mines.length
                  ? `A ${form.plant} use case belongs to one of that group's own mines.`
                  : undefined
              }
              {...hl("dept_shop")}
            >
              {mines.length ? (
                <Select
                  value={form.dept_shop || ""}
                  onValueChange={(v) => set("dept_shop", v)}
                  disabled={!can.rest}
                >
                  <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-dept-shop">
                    <SelectValue placeholder="Which mine" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 rounded-none bg-popover">
                    {mines.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <SuggestInput
                  field="dept_shop"
                  testId="pf-dept-shop"
                  placeholder="e.g. SMS-II"
                  value={form.dept_shop ?? ""}
                  disabled={!can.rest}
                  onChange={(v) => set("dept_shop", v)}
                />
              )}
            </Field>
            <Field label="Project Owner" {...hl("owner_name")}>
              <SuggestInput
                field="owner_name"
                testId="pf-owner-name"
                placeholder="e.g. AGM (Operations)"
                value={form.owner_name ?? ""}
                disabled={!can.rest}
                onChange={(v) => set("owner_name", v)}
              />
            </Field>
          </Step>

          <Step
            n={8}
            title="Investment and Returns"
            open={can.rest}
            lock="Say where it stands first"
            testId="pf-step-money"
          >
            <Field label="Nature of investment" {...hl("approval_route")}>
              <Select
                value={form.approval_route}
                onValueChange={(v) => set("approval_route", v)}
                disabled={!can.rest}
              >
                <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="pf-approval-route">
                  <SelectValue placeholder="CAPEX / OPEX / Revenue" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-popover">
                  {meta?.investment_natures?.map((n) => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cost in ₹ Lakh" {...hl("project_cost_lakh")}>
              <Input
                type="number"
                step="0.01"
                className="font-data mt-1 h-11 rounded-none"
                data-testid="pf-cost"
                value={form.project_cost_lakh}
                disabled={!can.rest}
                onChange={(e) => set("project_cost_lakh", e.target.value)}
              />
            </Field>
            <Field label="Expected benefit in ₹ Crore" {...hl("est_value_cr")}>
              <Input
                type="number"
                step="0.01"
                className="font-data mt-1 h-11 rounded-none"
                data-testid="pf-benefit"
                value={form.est_value_cr}
                disabled={!can.rest}
                onChange={(e) => set("est_value_cr", e.target.value)}
              />
            </Field>
            <Field
              label={
                isSafety
                  ? "Benefit certified by Unit Safety Head, ₹ Crore"
                  : "Benefit vetted by Finance, ₹ Crore"
              }
              {...hl("actual_benefit_cr")}
            >
              <Input
                type="number"
                step="0.01"
                className="font-data mt-1 h-11 rounded-none"
                data-testid="pf-actual-benefit"
                placeholder={
                  isSafety
                    ? "Only what the Unit Safety Head has certified"
                    : "Only what Finance has vetted"
                }
                value={form.actual_benefit_cr}
                disabled={!can.rest}
                onChange={(e) => set("actual_benefit_cr", e.target.value)}
              />
            </Field>
            <Field
              label={
                isSafety
                  ? "Date of certification by Unit Safety Head"
                  : "Date of financial vetting"
              }
              hint={form.actual_benefit_cr ? "Compulsory with a vetted figure." : undefined}
              {...gapProps("finance_vetted_on")}
              {...hl("finance_vetted_on")}
            >
              <DayField
                className="mt-1"
                tall
                testId="pf-finance-vetted-on"
                value={form.finance_vetted_on}
                disabled={!can.rest}
                onChange={(v) => set("finance_vetted_on", v)}
              />
            </Field>
          </Step>

          <Step
            n={9}
            title="Milestone dates"
            note={`${roadLabel} · expected on the left, actual on the right`}
            open={can.rest && !!milestones.length}
            lock="Say who is building it, and where it stands"
            testId="pf-step-milestones"
            single
          >
            <div className="divide-y divide-border border border-border" data-testid="pf-milestones">
              <div className="hidden gap-2 bg-muted/40 px-3 py-2 sm:grid sm:grid-cols-3">
                <span className="label-caps">Expected date</span>
                <span className="label-caps">Actual date</span>
                <span className="label-caps">Why it moved</span>
              </div>
              {milestones.map((stage) => {
                const rec = held[stage] || {};
                const row = dates[stage] || {};
                const view = (project?.stage_dates_view || []).find((v) => v.stage === stage);
                const expected = row.expected_on ?? "";
                const completed = row.completed_on ?? "";
                const moving =
                  (expected.trim() && rec.expected_on && expected.trim() !== rec.expected_on) ||
                  (completed.trim() && rec.completed_on && completed.trim() !== rec.completed_on);
                return (
                  <div key={stage} className="p-3" data-testid={`pf-milestone-${stage}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">{stage}</span>
                      {stage === form.phase && (
                        <span className="border border-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                          Standing here
                        </span>
                      )}
                      {view?.revision && (
                        <span
                          className="border border-[hsl(var(--at-risk))] px-1.5 py-0.5 font-data text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--at-risk))]"
                          data-testid={`pf-milestone-revision-${stage}`}
                        >
                          {view.revision}
                        </span>
                      )}
                      {view?.label && (
                        <span className="font-data text-[11px] text-muted-foreground">
                          {view.label}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      <DayField
                        testId={`pf-milestone-expected-${stage}`}
                        placeholder={
                          rec.expected_on ? `expected: ${rec.expected_on}` : "expected dd.mm.yyyy"
                        }
                        value={expected}
                        onChange={(v) => setDate(stage, "expected_on", v)}
                      />
                      <DayField
                        testId={`pf-milestone-actual-${stage}`}
                        placeholder={
                          rec.completed_on ? `actual: ${rec.completed_on}` : "actual dd.mm.yyyy"
                        }
                        value={completed}
                        onChange={(v) => setDate(stage, "completed_on", v)}
                      />
                      <Input
                        className="h-10 rounded-none text-xs"
                        data-testid={`pf-milestone-reason-${stage}`}
                        placeholder={moving ? "why it is moving (compulsory)" : "why (optional)"}
                        value={row.reason ?? ""}
                        onChange={(e) => setDate(stage, "reason", e.target.value)}
                      />
                    </div>
                    {!!(view?.revisions || []).length && (
                      <ul className="mt-1 space-y-0.5">
                        {view.revisions.map((r) => (
                          <li
                            key={r.revision}
                            className="font-data text-[10px] text-muted-foreground"
                          >
                            {r.revision} · {r.date}
                            {r.reason ? ` — ${r.reason}` : ""} · {r.by}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </Step>

          <Step
            n={10}
            title="In your own words"
            open={can.rest}
            lock="Say where it stands first"
            testId="pf-step-words"
            single
          >
            <div {...(highlight === "scope" ? { className: "next-fill", "data-next": "yes" } : {})}>
              <label className="label-caps">What it does</label>
              <Textarea
                className="mt-1 rounded-none"
                rows={3}
                data-testid="pf-scope"
                value={form.scope}
                disabled={!can.rest}
                onChange={(e) => set("scope", e.target.value)}
              />
            </div>
            <div
              {...(highlight === "remarks" ? { className: "next-fill", "data-next": "yes" } : {})}
            >
              <label className="label-caps">Where it stands today</label>
              <Textarea
                className="mt-1 rounded-none"
                rows={3}
                data-testid="pf-remarks"
                placeholder="What has happened and what is pending."
                value={form.remarks}
                disabled={!can.rest}
                onChange={(e) => set("remarks", e.target.value)}
              />
            </div>
          </Step>

          <Step
            n={11}
            title="What may be shared outside the unit"
            open={can.rest}
            lock="Say where it stands first"
            testId="pf-step-sharing"
            single
          >
            <div className="space-y-2">
              {[
                ["share_ministry", "Shared with Ministry — carried in the Ministry pack"],
                [
                  "share_corporate",
                  "Shared with Corporate Office — carried in the SteerCo Meeting pack",
                ],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`pf-${key.replace("_", "-")}`}
                    className="h-4 w-4 accent-[hsl(var(--primary))]"
                    checked={!!form[key]}
                    disabled={!can.rest}
                    onChange={(e) => set(key, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </Step>

          {watch?.card && (
            <Step
              n={12}
              title="Compliance data"
              note={`${watch.card.compliance.days} day(s) on record · cumulative ${fmtPct(
                watch.card.compliance.cumulative_pct,
              )} · last filled ${watch.card.last_updated || "never"}`}
              open
              testId="pf-step-compliance"
              single
            >
              <div className="space-y-4" data-testid="pf-compliance">
                <OneTimeForm
                  card={watch.card}
                  canEdit={watch.can_record}
                  testId={`pf-one-time-${watch.card.code}`}
                  onSaved={() => setWatchBeat((b) => b + 1)}
                />
                {watch.can_record ? (
                  <DailyEntryForm
                    card={watch.card}
                    testId={`pf-daily-${watch.card.code}`}
                    onSaved={() => setWatchBeat((b) => b + 1)}
                  />
                ) : (
                  <p
                    className="border border-border bg-muted/30 p-3 text-xs text-muted-foreground"
                    data-testid="pf-compliance-readonly"
                  >
                    These readings belong to {watch.card.plant} — its own SPOC or SDTD fills them.
                  </p>
                )}
                <p className="text-[10.5px] leading-relaxed text-muted-foreground">
                  This use case is sending compliance data, so its scale and its days are filled
                  here as well as on the Compliance Data screen — there is still only one copy of
                  every figure.
                </p>
              </div>
            </Step>
          )}

          {superAdmin && mode === "edit" && (
            <Step
              n={13}
              title="The portal's own stamps"
              note="Super-Admin mode — correcting these is not recorded anywhere"
              open
              testId="pf-stamps"
            >
              {[
                ["last_updated_by", "Last updated by", false],
                ["last_updated_on", "Last updated on", true],
                ["entered_by", "Entered by", false],
                ["entered_on", "Entered on", true],
                ["approved_by", "Approved by", false],
                ["approved_on", "Approved on", true],
              ].map(([key, label, isDate]) => (
                <Field key={key} label={label} hint={isDate ? "dd.mm.yyyy" : undefined}>
                  {isDate ? (
                    <DayField
                      className="mt-1"
                      tall
                      testId={`pf-${key.replace(/_/g, "-")}`}
                      value={form[key] || ""}
                      onChange={(v) => set(key, v)}
                    />
                  ) : (
                    <Input
                      className="mt-1 h-11 rounded-none"
                      data-testid={`pf-${key.replace(/_/g, "-")}`}
                      value={form[key] || ""}
                      onChange={(e) => set(key, e.target.value)}
                    />
                  )}
                </Field>
              ))}
            </Step>
          )}

          {!!highlight && (
            <div
              data-testid="pf-next-line"
              className="sticky bottom-0 z-10 mt-4 flex items-center justify-between gap-3 border border-primary/50 bg-card/95 px-3 py-2 backdrop-blur"
            >
              <span className="min-w-0 truncate text-[11px]">
                <span className="label-caps">Next</span>{" "}
                <span className="font-semibold" data-testid="pf-next-label">
                  {highlightLabel}
                </span>
              </span>
              <button
                type="button"
                data-testid="pf-next-jump"
                onClick={() => jumpTo(testIdOf(highlight))}
                className="shrink-0 border border-border px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
              >
                Take me there
              </button>
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button
              type="submit"
              disabled={busy}
              data-testid={mode === "add" ? "save-new-project-button" : "save-project-button"}
              className="h-11 w-full rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              {busy ? "Saving…" : mode === "add" ? "Add to the register" : "Save everything"}
            </Button>
          </DialogFooter>
        </form>

        <aside className="hidden lg:block" data-testid="pf-checklist">
          <div className="sticky top-0 border border-border bg-card p-3">
            <div className="label-caps">How far down the form</div>
            <div className="mt-2 space-y-0.5">
              {steps.map(([n, title, testId]) => {
                const mine = flow.filter((f) => f.step === n && f.need);
                const done = mine.filter((f) => f.done).length;
                const here = highlighted?.step === n;
                const whole = mine.length > 0 && done === mine.length;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`pf-check-${n}`}
                    data-state={whole ? "done" : here ? "here" : "open"}
                    onClick={() => jumpTo(testId)}
                    className={`flex w-full items-center gap-2 border-l-2 px-2 py-1.5 text-left text-[11px] transition-colors duration-200 ${
                      here
                        ? "border-primary bg-primary/5 text-foreground"
                        : stepOpen[n]
                          ? "border-transparent text-muted-foreground hover:bg-muted"
                          : "border-transparent text-muted-foreground/50"
                    }`}
                  >
                    <span className="font-data text-[10px]">{String(n).padStart(2, "0")}</span>
                    <span className="min-w-0 flex-1 truncate">{title}</span>
                    {whole ? (
                      <Check className="h-3 w-3 text-[hsl(var(--healthy))]" />
                    ) : mine.length ? (
                      <span className="font-data text-[10px] tabular-nums">
                        {done}/{mine.length}
                      </span>
                    ) : (
                      <span className="font-data text-[10px] text-muted-foreground">·</span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 border-t border-border pt-2 text-[10px] text-muted-foreground">
              The ring on the form shows the one entry it is waiting on. It moves on as each one is
              answered.
            </p>
          </div>
        </aside>
        </div>

        <ConfirmDialog
          open={!!ask}
          onOpenChange={(v) => !v && setAsk(null)}
          title={mode === "add" ? "Add this use case?" : "Are you sure?"}
          description={
            mode === "add"
              ? "The code is given by the portal and your name with today's date is saved against it."
              : `${project?.code} — ${project?.title}. This is what will change; you can take it back for 20 seconds after saving.`
          }
          changes={
            mode === "add"
              ? [
                  { label: "Unit", from: null, to: form.plant },
                  { label: "Use case", from: null, to: form.title },
                  { label: "Status", from: null, to: form.status },
                  { label: "Sub-stage", from: null, to: form.phase || "not applicable" },
                ]
              : ask || []
          }
          confirmLabel={mode === "add" ? "Yes, add it" : "Yes, save it"}
          testId="project-form-confirm"
          onConfirm={() => save(ask || [])}
        />
      </DialogContent>
    </Dialog>
  );
};
